/**
 * Manus API Configuration Utilities
 * Quản lý cấu hình API từ environment variables
 */

/**
 * Check if running in browser environment
 */
const isBrowser = typeof window !== 'undefined';

/**
 * Check if we should use backend proxy to avoid CORS
 * ✅ FIX: Use proxy on production (Vercel) to avoid CORS block
 * 
 * Manus API không allow direct browser calls do CORS policy
 * Phải gọi qua proxy: https://aiprice.youhomes.vn/api/manus
 * 
 * Local dev: Có thể tắt proxy nếu có dev server khác
 */
const shouldUseProxy = () => {
  // ✅ Use proxy in browser environment (to avoid CORS)
  // FE calls proxy endpoint: https://aiprice.youhomes.vn/api/manus
  // Proxy forwards to: https://api.manus.ai/v1 (with API key)
  
  // 🔧 OPTION: Set to false to call backend directly on port 3001 (CORS must be configured)
  // return false;
  
  return isBrowser;
};

/**
 * Lấy cấu hình Manus API từ environment variables
 */
export const getManusConfig = () => {
  const useProxy = shouldUseProxy();
  const directApiUrl = process.env.REACT_APP_MANUS_API_URL || "https://api.manus.ai/v1";

  // Determine API URL based on environment
  let apiUrl;
  if (useProxy) {
    // ✅ Production (Vercel): Use proxy to avoid CORS
    // Browser calls: https://aiprice.youhomes.vn/api/manus
    // Vercel forwards to: https://api.manus.ai/v1 (with API key)
    apiUrl = "/api/manus";
  } else {
    // Direct API (local Node.js or SSR)
    apiUrl = directApiUrl;
  }

  return {
    apiUrl,
    apiKey: process.env.REACT_APP_MANUS_API_KEY || "",
    agentProfile: process.env.REACT_APP_MANUS_AGENT_PROFILE || "manus-1.5-lite",
    taskMode: process.env.REACT_APP_MANUS_TASK_MODE || "chat",
    locale: process.env.REACT_APP_MANUS_LOCALE || "vi-VN",
    createShareableLink: process.env.REACT_APP_MANUS_SHAREABLE === "true",
    hideInTaskList: process.env.REACT_APP_MANUS_HIDE_IN_LIST !== "false",

    enableManusAI: process.env.REACT_APP_ENABLE_MANUS_AI !== "false",
    fallbackToMock: process.env.REACT_APP_FALLBACK_TO_MOCK === "true",
    debug: process.env.REACT_APP_DEBUG === "true",
    useProxy, // Expose this for debugging

    requestTimeout: parseInt(
      process.env.REACT_APP_MANUS_TIMEOUT || "120000",
      10
    ),
    pollingIntervalMs: parseInt(
      process.env.REACT_APP_MANUS_POLL_INTERVAL || "4000",
      10
    ),
    pollingTimeoutMs: parseInt(
      process.env.REACT_APP_MANUS_POLL_TIMEOUT || "600000",
      10
    ),
  };
};

/**
 * Kiểm tra xem Manus API có được bật không
 */
export const isManusEnabled = () => {
  return getManusConfig().enableManusAI;
};

/**
 * Kiểm tra xem có fallback về mock data không
 */
export const shouldFallbackToMock = () => {
  return getManusConfig().fallbackToMock;
};

/**
 * Log debug message nếu debug mode được bật
 */
export const debugLog = (message, data = null) => {
  const config = getManusConfig();

  // Chỉ log khi debug = true và không phải production
  if (config.debug && process.env.NODE_ENV !== "production") {
    if (data) {
      console.log(`🔧 [Manus Debug] ${message}:`, data);
    } else {
      console.log(`🔧 [Manus Debug] ${message}`);
    }
  }
};

/**
 * Tạo unique request ID
 */
export const generateRequestId = (prefix = "manus") => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Validate cấu hình API
 */
export const validateManusConfig = () => {
  const config = getManusConfig();
  const errors = [];

  if (!config.apiUrl) {
    errors.push("REACT_APP_MANUS_API_URL is required");
  }

  if (!config.apiKey) {
    errors.push("REACT_APP_MANUS_API_KEY is required");
  }

  try {
    new URL(config.apiUrl);
  } catch (e) {
    errors.push("REACT_APP_MANUS_API_URL must be a valid URL");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get WebSocket URL từ HTTP URL
 */
export const getWebSocketUrl = (httpUrl) => {
  return httpUrl.replace("http://", "ws://").replace("https://", "wss://");
};

/**
 * Format error message cho display
 */
export const formatErrorMessage = (error) => {
  if (typeof error === "string") {
    return error;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.message) {
    return error.message;
  }

  return "Có lỗi không xác định xảy ra";
};

/**
 * Retry function với exponential backoff
 */
export const retryWithBackoff = async (
  fn,
  maxRetries = 3,
  baseDelay = 1000
) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (i === maxRetries - 1) {
        break; // Don't wait after the last attempt
      }

      const delay = baseDelay * Math.pow(2, i);
      debugLog(`Retry attempt ${i + 1} failed, waiting ${delay}ms`, error);

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

const manusConfig = {
  getManusConfig,
  isManusEnabled,
  shouldFallbackToMock,
  debugLog,
  generateRequestId,
  validateManusConfig,
  getWebSocketUrl,
  formatErrorMessage,
  retryWithBackoff,
};

export default manusConfig;
